<script>
import V4Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import ReportHeader from './components/ReportHeader.vue';
import FilterSelector from './components/FilterSelector.vue';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import Spinner from 'shared/components/Spinner.vue';
import ReportsAPI from 'dashboard/api/reports';
import BarChart from 'shared/components/charts/BarChart.vue';

export default {
  name: 'LabelSummaryReports',
  components: {
    V4Button,
    ReportHeader,
    FilterSelector,
    TableHeaderCell,
    Spinner,
    BarChart,
  },
  data() {
    return {
      from: 0,
      to: 0,
      businessHours: false,
      isLoading: false,
      rows: [],
      chartByLabel: { labels: [], datasets: [] },
    };
  },
  computed: {
    labels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
  },
  methods: {
    async fetchData() {
      if (!this.from || !this.to) return;
      this.isLoading = true;
      try {
        const rows = [];
        const labels = [];
        const convs = [];
        const incoming = [];
        for (const label of this.labels) {
          const res = await ReportsAPI.getSummary(
            this.from,
            this.to,
            'label',
            label.id,
            'day',
            this.businessHours
          );
          rows.push({
            id: label.id,
            title: label.title,
            color: label.color,
            conversations_count: res.data.conversations_count || 0,
            incoming_messages_count: res.data.incoming_messages_count || 0,
          });
          labels.push(label.title);
          convs.push(res.data.conversations_count || 0);
          incoming.push(res.data.incoming_messages_count || 0);
        }
        // sort by conversations desc
        rows.sort((a, b) => (b.conversations_count || 0) - (a.conversations_count || 0));
        this.rows = rows;

        // build comparison chart per label
        this.chartByLabel = {
          labels,
          datasets: [
            { type: 'bar', backgroundColor: 'rgb(31,147,255)', label: this.$t('LABEL_SUMMARY.CHARTS.CONVERSATIONS'), data: convs },
            { type: 'bar', backgroundColor: '#0F766E', label: this.$t('LABEL_SUMMARY.CHARTS.INCOMING'), data: incoming },
          ],
        };
      } catch (e) {
        useAlert(this.$t('LABEL_SUMMARY.FETCH_FAILED'));
      } finally {
        this.isLoading = false;
      }
    },
    loadScript(src) {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', e => reject(e));
          if (existing.readyState === 'complete') resolve();
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = e => reject(e);
        document.body.appendChild(s);
      });
    },
    async downloadPdf() {
      await this.loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js');
      await this.loadScript('https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js');
      const { jsPDF } = window.jspdf || {};
      const html2canvas = window.html2canvas;
      if (!jsPDF || !html2canvas) return;

      const node = this.$refs.pageRoot;
      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const doc = new jsPDF('p', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
      const imgWidth = canvas.width * ratio;
      const imgHeight = canvas.height * ratio;
      const x = (pageWidth - imgWidth) / 2;
      const y = 20;
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgWidth, imgHeight, undefined, 'FAST');
      doc.save('label-summary.pdf');
    },
    onFilterChange({ from, to, groupBy, businessHours }) {
      this.from = from;
      this.to = to;
      this.businessHours = businessHours;
      this.fetchData();
    },
  },
};
</script>

<template>
  <div ref="pageRoot">
    <ReportHeader :header-title="$t('LABEL_SUMMARY.HEADER')">
      <V4Button
        :label="$t('LABEL_SUMMARY.REFRESH')"
        icon="i-ph-arrow-clockwise"
        size="sm"
        @click="fetchData"
      />
      <V4Button
        class="ms-2"
        :label="$t('LABEL_SUMMARY.DOWNLOAD_PDF')"
        icon="i-ph-file-pdf"
        size="sm"
        @click="downloadPdf"
      />
    </ReportHeader>
    <div class="flex flex-col gap-3">
    <FilterSelector
      :show-agents-filter="false"
      :show-labels-filter="false"
      :show-inbox-filter="false"
      :show-team-filter="false"
      show-group-by-filter
      @filter-change="onFilterChange"
    />
    <div class="h-72 p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2">
      <div class="text-sm font-medium mb-2">{{ $t('LABEL_SUMMARY.TITLES.BY_LABEL') }}</div>
      <BarChart :collection="chartByLabel" />
    </div>
    <div class="min-w-full shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6">
      <div class="grid content-center h-12 grid-cols-12 gap-4 px-6 py-0 bg-n-slate-2 rounded-md">
        <TableHeaderCell :span="6" :label="$t('LABEL_SUMMARY.TABLE.HEADER.LABEL')" />
        <TableHeaderCell :span="3" :label="$t('LABEL_SUMMARY.TABLE.HEADER.CONVERSATIONS')" />
        <TableHeaderCell :span="3" :label="$t('LABEL_SUMMARY.TABLE.HEADER.INCOMING_MESSAGES')" />
      </div>
      <div v-if="isLoading" class="flex items-center justify-center h-32">
        <Spinner />
      </div>
      <div v-else>
        <div
          v-for="row in rows"
          :key="row.id"
          class="grid items-center grid-cols-12 gap-4 px-6 py-3 border-b border-n-weak"
        >
          <div class="col-span-6 flex items-center gap-2">
            <span :style="{ backgroundColor: row.color }" class="w-3 h-3 rounded-full" />
            <span class="text-sm">{{ row.title }}</span>
          </div>
          <div class="col-span-3 text-sm">{{ row.conversations_count?.toLocaleString() }}</div>
          <div class="col-span-3 text-sm">{{ row.incoming_messages_count?.toLocaleString() }}</div>
        </div>
        <div v-if="!rows.length" class="flex items-center justify-center h-24 text-sm text-slate-600">
          {{ $t('REPORT.NO_ENOUGH_DATA') }}
        </div>
      </div>
    </div>
    </div>
  </div>
</template>


